import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import LoadingOverlay from '../components/ui/LoadingOverlay';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'complete' | 'error';
}

const INITIAL_STEPS: LoadingStep[] = [
  { id: 'tokens', label: 'Refreshing email tokens', status: 'pending' },
  { id: 'messages', label: 'Syncing messages', status: 'pending' }
];
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([]);

  const updateStepStatus = (stepId: string, status: LoadingStep['status']) => {
    setLoadingSteps(steps => 
      steps.map(step => 
        step.id === stepId ? { ...step, status } : step
      )
    );
  };

  const refreshEmailTokens = async () => {
    try {
      const { data: accounts } = await supabase
        .from('nrm_gmail_credentials')
        .select('*');

      if (!accounts?.length) return;

      for (const account of accounts) {
        try {
          const { data: tokens } = await supabase.functions.invoke('refresh-gmail-token', {
            body: { refreshToken: account.refresh_token }
          });

          await supabase
            .from('nrm_gmail_credentials')
            .update({
              access_token: tokens.access_token,
              token_expiry: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', account.id);
        } catch (err) {
          console.error(`Failed to refresh token for ${account.email}:`, err);
        }
      }
    } catch (err) {
      console.error('Failed to refresh email tokens:', err);
      throw err;
    }
  };

  const refreshMessages = async () => {
    try {
      const { data: contacts } = await supabase
        .from('nrm_contacts')
        .select('work_email, personal_email');

      if (!contacts?.length) return;

      const emails = contacts
        .map(contact => [contact.work_email, contact.personal_email])
        .flat()
        .filter((email): email is string => !!email);

      // Remove duplicates
      const uniqueEmails = [...new Set(emails)];

      // Refresh messages for each unique email
      for (const email of uniqueEmails) {
        try {
          const { data: accounts } = await supabase
            .from('nrm_gmail_credentials')
            .select('*');

          for (const account of accounts || []) {
            await supabase.functions.invoke('fetch-gmail-messages', {
              body: { 
                email,
                accessToken: account.access_token,
                accountEmail: account.email
              }
            });
          }
        } catch (err) {
          console.error(`Failed to refresh messages for ${email}:`, err);
        }
      }
    } catch (err) {
      console.error('Failed to refresh messages:', err);
      throw err;
    }
  };

  const handleInitialLoad = async () => {
    setLoadingSteps(INITIAL_STEPS);
    
    // Refresh email tokens
    updateStepStatus('tokens', 'loading');
    try {
      await refreshEmailTokens();
      updateStepStatus('tokens', 'complete');
    } catch (err) {
      updateStepStatus('tokens', 'error');
      return;
    }

    // // Refresh messages
    // updateStepStatus('messages', 'loading');
    // try {
    //   await refreshMessages();
    //   updateStepStatus('messages', 'complete');
    // } catch (err) {
    //   updateStepStatus('messages', 'error');
    // }

    // Clear loading steps after a delay
    setTimeout(() => setLoadingSteps([]), 1000);
  };

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    signIn: async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await handleInitialLoad();
    },
    signUp: async (email: string, password: string) => {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
    },
    signOut: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {loadingSteps.length > 0 && <LoadingOverlay steps={loadingSteps} />}
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}